import AddToListDropdownContainer from "../../opportunities/components/miscellaneous/AddToListDropdown/AddToListDropdownContainer";
import type { ReactElement } from "react";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import ListIconButtonTooltipContainer from "app/pages/sales-intelligence/sub-modules/account-review/ListIconButton/ListIconButtonTooltipContainer";
import { StyledTooltipListPart } from "app/pages/sales-intelligence/sub-modules/account-review/AddToListButton/styles";
import type { ButtonType } from "@similarweb/ui-components";
import getTooltipTranslation from "app/pages/sales-intelligence/sub-modules/account-review/AddToListButton/utils";
import accountReviewTrackingService from "app/pages/sales-intelligence/sub-modules/account-review/services/tracking/accountReviewTrackingService";

export type AddWebsiteToListButtonBaseProps = {
    domain: string;
    disabledText?: string;
    type?: ButtonType;
    isShortContent?: boolean;
    onButtonClick?(): void;
    onButtonHover?(): void;
};

type AddWebsiteToListButtonProps = AddWebsiteToListButtonBaseProps & {
    listsWithDomain: string[];
    isListSizeQuotaReached: boolean;
    isTrialTooltip?: boolean;
    limitOfWebsitesToTrack?: number;
    isDisabled: boolean;
    disabledListsNames: string[];
};

const AddWebsiteToListButton = (props: AddWebsiteToListButtonProps) => {
    const {
        domain,
        disabledText,
        type,
        isShortContent,
        disabledListsNames,
        isListSizeQuotaReached,
        isTrialTooltip = false,
        limitOfWebsitesToTrack,
        listsWithDomain,
        isDisabled,
        onButtonClick,
        onButtonHover,
    } = props;

    const translate = useTranslation();

    const showDefaultButtonText = listsWithDomain.length === 0 && !isShortContent;

    const handleListUpdate = () => {
        accountReviewTrackingService.trackAddToListSelect("existing list");
    };

    const handleListCreated = () => {
        accountReviewTrackingService.trackAddToListSelect("new list");
    };

    function renderTooltipContent(): ReactElement | undefined {
        if (!listsWithDomain.length) {
            if (showDefaultButtonText) {
                return undefined;
            }

            return <div>{translate("si.pages.account_review.add_to_list_button.tooltip")}</div>;
        }

        return (
            <div>
                <StyledTooltipListPart>{getTooltipTranslation(translate, listsWithDomain)}</StyledTooltipListPart>
                <div>{translate("si.pages.account_review.add_to_list_button.tooltip.addHint")}</div>
            </div>
        );
    }

    function renderListIconButton(onClick: () => void) {
        const buttonText = showDefaultButtonText
            ? translate("si.pages.account_review.add_to_list_button.addToListTitle")
            : listsWithDomain.length;

        return (
            <ListIconButtonTooltipContainer
                onClick={() => {
                    onButtonClick?.();
                    onClick();
                }}
                onMouseEnter={onButtonHover}
                buttonText={buttonText || undefined}
                tooltipContent={renderTooltipContent()}
                type={type}
                isDisabled={isDisabled}
                isListSizeQuotaReached={isListSizeQuotaReached}
                isTrialTooltip={isTrialTooltip}
                limitOfWebsitesToTrack={limitOfWebsitesToTrack}
            />
        );
    }

    return (
        <AddToListDropdownContainer
            disabledText={disabledText}
            disabledLists={disabledListsNames}
            domains={[domain]}
            renderDropdownButton={renderListIconButton}
            onListUpdate={handleListUpdate}
            onFinishedCreatingList={handleListCreated}
        />
    );
}

export default AddWebsiteToListButton;
